import * as R from 'ramda';
import storage from '../../../hooks/useStorage/useStorage';
import onlineProductDetailsRepository from '../../../services/modules/productDetails/onlineProductDetails.repository';
import {
  IProductDetailsFilter,
  ProductDetailsMedias,
  ProductDetailsSize,
  ProductDetailsSummary,
  ValidateCombinationsDTO,
} from '../../../services/modules/productDetails/productDetails.types';
import useGeneralSettingsStore from '../../../stores/GeneralSettings/useGeneralSettingsStore';
import { ArrayUtil } from '../../../utils/array.utils';
import { ESProductSearch } from '../../products/ProductTypes';
import { ESProduct } from '../../products/product.types.v2';
import {
  GetProductDetailsEmptyAvailabilitiesOutput,
  ProductDetailsDTO,
  ProductDetailsGroupSection,
} from '../../products/products-details-v2/types/productDetails.types';
import { offlineClassificationsRepository } from '../repositories/offlineClassifications.repository';
import { offlineOrdersRepository } from '../repositories/offlineOrders.repository';
import { offlineProductColorsRepository } from '../repositories/offlineProductColors.repository';
import { offlineProductsRepository } from '../repositories/offlineProducts.repository';
import { offlineSizesRepository } from '../repositories/offlineSizes.repository';
import { OfflineSizeTransformers } from '../transformers/OfflineDatabaseSize.transformers';
import OfflineProductColorToProductDetailsTransformers from '../transformers/productColor/OfflineDatabaseProductColor.toProductDetails.transformers';
import { createApiSauceResponse } from '../utils/createApiSauceResponse.util';
import { createFetcherOfflineImplementation } from '../utils/createFetcherOfflineImplementation.util';
import ProductDetailsFilterUtils from './utils/productDetails/productDetailsFilter.utils';
import ProductDetailsPasteGridUtils from './utils/productDetails/productDetailsPasteGrid.utils';
import ProductDetailsQueryUtils from './utils/productDetails/productDetailsQuery.utils';

const onlineProductDetailsRepositoryOfflineAdapters = (): ReturnType<
  typeof onlineProductDetailsRepository
> => {
  return {
    getProductDetailsEmptyAvailabilities: async ({ orderId }) => {
      if (!orderId) {
        throw new Error('[getProductDetailsEmptyAvailabilities] orderId is required ');
      }

      const order = await offlineOrdersRepository.getById(orderId);
      if (!order) {
        throw new Error(`[getProductDetailsEmptyAvailabilities] order ${orderId} not found`);
      }

      const emptyOrderProducts = order.getEmptyOrderProducts();

      return createApiSauceResponse<GetProductDetailsEmptyAvailabilitiesOutput[]>({
        ok: true,
        data: emptyOrderProducts.map((product) => ({
          orderId,
          productColorId: product.productColor.id,
        })),
      });
    },

    getProductDetailsProductColor: async (params) => {
      const offlineProductColor = await offlineProductColorsRepository.getByIdOrFail(
        params.productColorId,
      );

      const formatted =
        await OfflineProductColorToProductDetailsTransformers.toProductDetailsESProductSearch(
          offlineProductColor,
          params.orderId,
        );

      return createApiSauceResponse<ESProductSearch>({ ok: true, data: formatted });
    },

    getProductDetailsFilter: async (params) => {
      const [colors, availabilities, deliveryWindows] = await Promise.all([
        ProductDetailsFilterUtils.getColorsFilter(params),
        ProductDetailsFilterUtils.getAvailabilitiesFilter(params),
        ProductDetailsFilterUtils.getDeliveryWindowFilter(params),
      ]);

      return createApiSauceResponse<IProductDetailsFilter>({
        ok: true,
        data: { colors, availabilities, deliveryWindows },
      });
    },

    getProductDetailsMedias: async ({ productId }) => {
      const productColors = await offlineProductColorsRepository.findByProductId(productId);
      const ordered = R.sortBy((productColor) => productColor.name.toLowerCase(), productColors);

      return createApiSauceResponse<ProductDetailsMedias>({
        ok: true,
        data: {
          productId,
          colors: ordered.map(
            OfflineProductColorToProductDetailsTransformers.toProductDetailsMediaColor,
          ),
        },
      });
    },

    getProductDetailsCombinations: async (params) => {
      const brandId = storage.getBrandId();
      if (!brandId) {
        throw new Error('[getProductDetailsCombinations] brandId is required in offline flow');
      }
      const brandGeneralSettings = useGeneralSettingsStore.getState().generalSettings;

      const data = await ProductDetailsQueryUtils.getCombinations(
        params,
        !!brandGeneralSettings?.restrictProductsUsingCustomerCatalogs,
      );
      const paginated = ArrayUtil.paginate(data, params.skip, params.limit);

      return createApiSauceResponse<ProductDetailsDTO[], ProductDetailsDTO[]>({
        ok: true,
        data: paginated,
      });
    },

    getProductDetailsGroupSections: async (params) => {
      const data = await ProductDetailsQueryUtils.getCombinationsCount(params);

      return createApiSauceResponse<ProductDetailsGroupSection[]>({
        ok: true,
        data,
      });
    },

    getProductDetailsSummary: async (params) => {
      const data = await ProductDetailsQueryUtils.getSummary(params);

      return createApiSauceResponse<ProductDetailsSummary, ProductDetailsSummary>({
        ok: true,
        data,
      });
    },

    getProductDetailsSizes: async (params) => {
      if (!params.productId) {
        throw new Error('[getProductDetailsSizes] productId is required in offline flow');
      }

      const product = await offlineProductsRepository.getByIdOrFail(params.productId);
      const sizes = await offlineSizesRepository.findByIds(product.sizeIds);
      const ordered = R.sortBy((size) => size.position, sizes);

      return createApiSauceResponse<ProductDetailsSize[], ProductDetailsSize[]>({
        ok: true,
        data: ordered.map(OfflineSizeTransformers.toProductDetailsSize),
      });
    },

    getSuggestedProducts: (productColorId) => {
      return createFetcherOfflineImplementation<ESProduct>(async (params) => {
        const suggestedClassificationTypeId =
          useGeneralSettingsStore.getState().generalSettings.suggestedClassificationType?.id;

        if (!suggestedClassificationTypeId) {
          return { count: 0, data: [] };
        }

        const productColor = await offlineProductColorsRepository.getByIdOrFail(productColorId);

        const classifications =
          await offlineClassificationsRepository.findByIdsAndClassificationTypeId(
            productColor.classificationIds,
            suggestedClassificationTypeId,
          );

        const relatedProductColors = await offlineProductColorsRepository.findRelatedProductColors(
          [productColor.id],
          classifications.map((c) => c.id),
        );

        const paginated = ArrayUtil.paginate(relatedProductColors, params.skip, params.limit);

        const transformed =
          await OfflineProductColorToProductDetailsTransformers.toProductDetailsESProduct(
            paginated,
            params.params?.orderId,
          );

        return { count: relatedProductColors.length, data: transformed };
      });
    },

    postBatchPasteGrid: async (params) => {
      await ProductDetailsPasteGridUtils.batchPasteGrid(params);
      return createApiSauceResponse({ ok: true });
    },

    getCombinationsValidation: async (
      _params: Parameters<
        ReturnType<typeof onlineProductDetailsRepository>['getCombinationsValidation']
      >[0],
    ) => {
      return createApiSauceResponse<ValidateCombinationsDTO>({
        ok: true,
        data: { ordersErrors: {} },
      });
    },
  };
};

export default onlineProductDetailsRepositoryOfflineAdapters;
